import { Component, forwardRef, Inject, OnDestroy } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { hasEventDetailsContext } from '../../common/helper';
import { Alert } from '../../model';
import { NavigationService } from '../../service/navigation.service';
import { ThingContextService } from '../../service/thing-context.service';
import { ThingService } from '../../service/thing.service';
import { DurationFormatterPipe } from '../../shared/pipe';
import { getThingContextServiceProvider } from '../../shared/provider/thing-context-service.provider';
import { LayoutType } from '../shared/details-widget';
import { LocationSchemaPlanViewThingData } from './location-schema-plan-viewer.component';

@Component({
    selector: 'work-session-details-page-dialog',
    template: `
        <h2 mat-dialog-title class="d-flex align-items-start">
            <div class="d-flex flex-column">
                <div>{{ thingData.thing.name | localize }}</div>
            </div>
            <button mat-icon-button mat-dialog-close class="ml-auto">
                <mat-icon>close</mat-icon>
            </button>
        </h2>
        <mat-dialog-content>
            <mat-progress-bar *ngIf="!loaded" mode="indeterminate"></mat-progress-bar>
            <ng-container *ngIf="loaded">
                <div class="d-flex flex-column" style="gap: 24px">
                    <thing-details-widget [inputRows]="thingData.components" [startDate]="range?.start?.valueOf()" [endDate]="range?.end?.valueOf()" [layout]="detailsLayout">
                    </thing-details-widget>
                    <event-details-box *ngFor="let e of events" [event]="e" [isHistorical]="false"></event-details-box>
                </div>
            </ng-container>
        </mat-dialog-content>
        <mat-dialog-actions class="justify-content-end" style="gap: 10px">
            <a class="btn btn-link" custom-label="goToThingPageLink" (click)="goToDetails()"></a>
        </mat-dialog-actions>
    `,
    styles: [require('./location-schema-plan-viewer-grid-element-details-dialog.component.css')],
    viewProviders: [getThingContextServiceProvider(null, false)]
})
export class LocationSchemaPlanViewerGridElementDialogComponent implements OnDestroy {

    loaded: boolean;
    thingData: LocationSchemaPlanViewThingData;
    events: Alert[];
    range: DateRange<moment.Moment>;
    detailsLayout: LayoutType;

    constructor(
        @Inject(forwardRef(() => ThingService)) private thingService: ThingService,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService,
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<LocationSchemaPlanViewerGridElementDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => DurationFormatterPipe)) private durationFormatterPipe: DurationFormatterPipe,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService
    ) {
        this.thingData = data.thingData;
        this.range = data.range;
        this.events = this.thingData.events.sort((e1, e2) => e1.startTimestamp - e2.startTimestamp);
        this.detailsLayout = data.detailsLayout;
        this.events.forEach(event => {
            event['duration'] = this.durationFormatterPipe.transform({ startTimestamp: event.startTimestamp, endTimestamp: null });
            event['showDetailsButton'] = hasEventDetailsContext(event);
        });
        this.updateContext();
    }

    ngOnDestroy(): void {
        this.resetContext();
    }

    private updateContext(): void {
        this.thingService.getThingById(this.thingData.thing.id, null).then(() => {
            this.thingContextService.getMetrics().then(() => {
                this.loaded = true;
            });
        });
    }

    private resetContext(): void {
        this.thingContextService.updateCurrentThing(null);
    }

    goToDetails(): void {
        this.dialogRef.close();
        setTimeout(() => this.navigationService.goToThingDetailPage(this.thingData.thing.id), 100);     // wait for ngOnDestroy to reset the context
    }
}